import React, { useState, useEffect, useRef } from "react";
import { products as allProducts } from "../data/products";
import { useReactToPrint } from 'react-to-print';
import { useToast } from './ToastProvider';
import { useTranslation } from 'react-i18next';
import { useUser } from './UserContext';
import emailjs from 'emailjs-com';

function formatCurrency(value, currency = 'TRY', rates = { USD: 32, EUR: 35 }) {
  let symbol = '₺';
  let val = value;
  if (currency === 'USD') { symbol = '$'; val = value / rates.USD; }
  if (currency === 'EUR') { symbol = '€'; val = value / rates.EUR; }
  return `${Number(val).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${symbol}`;
}

// Heroicons SVG ikonları
const PlusIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14m7-7H5" />
  </svg>
);
const MinusIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 12h12" />
  </svg>
);

// Kalem ikonu (Heroicons outline)
const EditIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="inline w-4 h-4 text-gray-400 ml-1 align-text-bottom">
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487a2.1 2.1 0 1 1 2.97 2.97L7.5 19.789l-4 1 1-4 12.362-12.302z" />
  </svg>
);

// Tabloda columns dizisini teklif tipine göre ayarla
// columns dizisini fonksiyon dışından kaldır

// Boş ürün satırı şablonu
const emptyRow = {
  code: "",
  quantity: 1,
  promo: "",
  totalQty: 1,
  price: 0,
  description: "",
  total: 0,
  vat: 0.20,
  lot: "",
  udi: "",
};

const MIN_ROWS = 0;

// SUT kodlarını badge olarak gösteren yardımcı fonksiyon
function renderSutCodes(value) {
  if (!value) return null;
  const codes = Array.isArray(value)
    ? value
    : String(value).split(/[,\s]+/).filter(Boolean);
  return codes.map((code, i) => (
    <span key={i} className="inline-block bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded mr-1 mb-0.5">{code}</span>
  ));
}

// Demo SUT kodları
const DEMO_SUT_CODES = [
  'A1234', 'B5678', 'C9012', 'D3456', 'E7890', 'F1122', 'G3344', 'H5566', 'I7788', 'J9900'
];

// Demo promosyon ürünleri kaldırıldı

function getToday() {
  const d = new Date();
  return d.toLocaleDateString() + ' ' + d.toLocaleTimeString();
}

// Yazdırılacak alanı fonksiyon haline getir
function PrintContent({ readonly }) {
  const { t } = useTranslation();
  // Tüm useState tanımlarını yap
  const draft = JSON.parse(localStorage.getItem('offerDraft') || '{}');
  const [items, setItems] = useState(() => draft.items || []);
  const [selectedRows, setSelectedRows] = useState([]); // Seçili satır indexleri
  // Popup state'leri kaldırılıyor, yerine inline edit için state ekleniyor
  const [editingCell, setEditingCell] = useState(null); // {row: idx, field: key}
  const [editingValue, setEditingValue] = useState("");
  const [recipient, setRecipient] = useState(draft.recipient || "Sayın Müşterimiz");
  const [offerDate] = useState(new Date().toLocaleDateString());
  const [offerNo] = useState("2024-001");
  const [note, setNote] = useState(draft.note || "");
  const [savedOffers, setSavedOffers] = useState([]);
  const [selectedOfferId, setSelectedOfferId] = useState(null);
  const [offerType, setOfferType] = useState(draft.offerType || 'yurtiçi');
  const [currency, setCurrency] = useState(draft.currency || 'TRY');
  const [customRates, setCustomRates] = useState(draft.customRates || { USD: 32, EUR: 35 });
  const printRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Teklif_${offerNo}`,
  });
  const toast = useToast();
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const OFFER_STATUSES = [
    { value: 'Taslak', color: 'bg-gray-200 text-gray-700' },
    { value: 'Gönderildi', color: 'bg-blue-100 text-blue-800' },
    { value: 'Kabul Edildi', color: 'bg-green-100 text-green-800' },
    { value: 'Reddedildi', color: 'bg-red-100 text-red-700' },
  ];
  const [status, setStatus] = useState('Taslak');
  const [autocompleteOptions, setAutocompleteOptions] = useState([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const autocompleteRef = useRef();
  const VAT_OPTIONS = [
    { value: 0.01, label: '%1' },
    { value: 0.08, label: '%8' },
    { value: 0.10, label: '%10' },
    { value: 0.20, label: '%20' },
  ];
  const [defaultVat, setDefaultVat] = useState(0.20);

  // columns dizisini offerType'dan sonra tanımla
  const columns = offerType === 'yurtdışı'
    ? [
        { key: "productName", label: t('Ürün Adı') },
        { key: "quantity", label: t('Sipariş Adedi') },
        { key: "totalQty", label: t('Toplam Adet') },
        { key: "price", label: t('Birim Alış Fiyatı') },
        { key: "vat", label: t('KDV') },
        { key: "description", label: t('Açıklama') },
        { key: "total", label: t('Sipariş Tutarı') },
        { key: "udi", label: t('UDI Kodu') },
      ]
    : [
        { key: "code", label: t('SUT Kodu') },
        { key: "quantity", label: t('Sipariş Adedi') },
        { key: "promo", label: t('Promosyon Ürün') },
        { key: "totalQty", label: t('Toplam Adet') },
        { key: "price", label: t('Birim Alış Fiyatı') },
        { key: "vat", label: t('KDV') },
        { key: "description", label: t('Açıklama') },
        { key: "total", label: t('Sipariş Tutarı') },
        { key: "udi", label: t('UDI Kodu') },
      ];
  // useState başlatırken localStorage'dan taslak yükle
  // const draft = JSON.parse(localStorage.getItem('offerDraft') || '{}');
  // const [items, setItems] = useState(() => draft.items || []);
  // const [selectedRows, setSelectedRows] = useState([]); // Seçili satır indexleri
  // Popup state'leri kaldırılıyor, yerine inline edit için state ekleniyor
  // const [editingCell, setEditingCell] = useState(null); // {row: idx, field: key}
  // const [editingValue, setEditingValue] = useState("");
  // const [recipient, setRecipient] = useState(draft.recipient || "Sayın Müşterimiz");
  // const [offerDate] = useState(new Date().toLocaleDateString());
  // const [offerNo] = useState("2024-001");
  // const [note, setNote] = useState(draft.note || "");
  // const [savedOffers, setSavedOffers] = useState([]);
  // const [selectedOfferId, setSelectedOfferId] = useState(null);
  // const [offerType, setOfferType] = useState(draft.offerType || 'yurtiçi');
  // const [currency, setCurrency] = useState(draft.currency || 'TRY');
  // const [customRates, setCustomRates] = useState(draft.customRates || { USD: 32, EUR: 35 });
  // const printRef = useRef();
  // const handlePrint = useReactToPrint({
  //   content: () => printRef.current,
  //   documentTitle: `Teklif_${offerNo}`,
  // });
  // const toast = useToast();
  // const [showPdfPreview, setShowPdfPreview] = useState(false);
  // const OFFER_STATUSES = [
  //   { value: 'Taslak', color: 'bg-gray-200 text-gray-700' },
  //   { value: 'Gönderildi', color: 'bg-blue-100 text-blue-800' },
  //   { value: 'Kabul Edildi', color: 'bg-green-100 text-green-800' },
  //   { value: 'Reddedildi', color: 'bg-red-100 text-red-700' },
  // ];
  // const [status, setStatus] = useState('Taslak');
  // const [autocompleteOptions, setAutocompleteOptions] = useState([]);
  // const [showAutocomplete, setShowAutocomplete] = useState(false);
  // const autocompleteRef = useRef();
  // const VAT_OPTIONS = [
  //   { value: 0.01, label: '%1' },
  //   { value: 0.08, label: '%8' },
  //   { value: 0.10, label: '%10' },
  //   { value: 0.20, label: '%20' },
  // ];
  // const [defaultVat, setDefaultVat] = useState(0.20);

  // Kayıtlı teklifleri localStorage'dan yükle
  useEffect(() => {
    const offers = JSON.parse(localStorage.getItem('offers') || '[]');
    setSavedOffers(offers);
    // Eğer başka sayfadan seçili teklif varsa onu yükle
    const selectedId = localStorage.getItem('selectedOfferId');
    if (selectedId) {
      const offer = offers.find(o => String(o.id) === String(selectedId));
      if (offer) {
        setRecipient(offer.recipient);
        setItems(offer.items);
        setNote(offer.note);
        setSelectedOfferId(offer.id);
        setStatus(offer.status || 'Taslak');
      }
      localStorage.removeItem('selectedOfferId');
    }
  }, []);

  // Satır eklerken varsayılan KDV uygula
  useEffect(() => {
    if (offerType === 'yurtdışı') {
      setDefaultVat(0);
      setItems(items => items.map(row => ({ ...row, vat: 0 })));
    }
  }, [offerType]);

  // Taslak kaydı: items, recipient, note, offerType, currency, customRates değiştiğinde localStorage'a yaz
  useEffect(() => {
    localStorage.setItem('offerDraft', JSON.stringify({
      items,
      recipient,
      note,
      offerType,
      currency,
      customRates
    }));
  }, [items, recipient, note, offerType, currency, customRates]);

  // generateOfferNo fonksiyonu ve subtotal, totalVAT, grandTotal hesaplamalarından sonra saveOfferWithItems fonksiyonunu tanımla
  function generateOfferNo() {
    const offers = JSON.parse(localStorage.getItem('offers') || '[]');
    const year = new Date().getFullYear();
    const last = offers.find(o => o.offerNo && o.offerNo.startsWith(year));
    let nextNo = 1;
    if (last) {
      const nums = offers
        .filter(o => o.offerNo && o.offerNo.startsWith(year))
        .map(o => parseInt(o.offerNo.split('-')[1], 10))
        .filter(Boolean);
      if (nums.length > 0) nextNo = Math.max(...nums) + 1;
    }
    return `${year}-${String(nextNo).padStart(3, '0')}`;
  }

  // subtotal, totalVAT, grandTotal hesaplamaları
  const subtotal = items.reduce((sum, i) => sum + i.total, 0);
  const totalVAT = items.reduce((sum, i) => sum + i.total * (i.vat || 0.2), 0);
  const grandTotal = subtotal + totalVAT;

  // saveOfferWithItems fonksiyonu
  const saveOfferWithItems = async (saveItems, saveStatus) => {
    if (saveItems.length === 0) return;
        const offer = {
      offerNo: generateOfferNo(),

      date: getToday(),
      recipient,
      note,
      subtotal,
      totalVAT,
      grandTotal,
      status: saveStatus || status,
      emailSent: false,
      items: saveItems.map(item => ({
        product_id: item.product_id || null,
        measure: item.measure || '',
        qty: item.quantity || item.qty || 1,
        desc: item.description || item.desc || ''
      }))
    };
    try {
      const res = await fetch('/api/offers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(offer)
      });
      if (res.ok) {
        toast.showToast(t('Teklif başarıyla kaydedildi!'), 'success');
        localStorage.removeItem('offerDraft');
      } else {
        toast.showToast('Teklif kaydedilemedi!', 'error');
      }
    } catch (err) {
      toast.showToast('Teklif kaydedilemedi!', 'error');
    }
  };

  // Teklifi kaydet
  const saveOffer = () => {
    if (items.length === 0) return;
    const offer = {
      id: Date.now(),
      offerNo: generateOfferNo(),
      date: getToday(),
      recipient,
      items,
      note,
      subtotal,
      totalVAT,
      grandTotal,
      status,
    };
    const offers = JSON.parse(localStorage.getItem('offers') || '[]');
    // Aynı teklif var mı kontrol et
    const isDuplicate = offers.some(o =>
      o.recipient === offer.recipient &&
      JSON.stringify(o.items) === JSON.stringify(offer.items) &&
      o.note === offer.note &&
      o.subtotal === offer.subtotal &&
      o.totalVAT === offer.totalVAT &&
      o.grandTotal === offer.grandTotal &&
      o.status === offer.status
    );
    if (isDuplicate) {
      toast.showToast(t('Aynı teklif zaten kayıtlı!'), 'warning');
      return;
    }
    offers.unshift(offer);
    localStorage.setItem('offers', JSON.stringify(offers));
    setSavedOffers(offers);
    toast.showToast(t('Teklif başarıyla kaydedildi!'), 'success');
    // handlePrint(); // Otomatik PDF almak istersen açabilirsin
    localStorage.removeItem('offerDraft');
  };

  // Proforma onay fonksiyonu
  const handleProformaApprove = () => {
    // Sadece Taslak veya Gönderildi statüsünde onaylanabilir
    if (status !== 'Taslak' && status !== 'Gönderildi') return;
    // UDI kodlarını oluştur
    const newItems = items.map((item, idx) => {
      if (!item.udi) {
        // Ürün tanımını bul
        const code = Array.isArray(item.code) ? item.code[0] : item.code;
        const product = allProducts.find(p => p.code === code);
        let lot = item.lot || (product ? product.lot : '');
        if (product && product.promo) {
          lot = `PROM${new Date().toISOString().slice(0,10).replace(/-/g,"")}-${idx+1}`;
        }
        return {
          ...item,
          lot,
          udi: generateUDI(product, lot)
        };
      }
      return item;
    });
    setItems(newItems);
    setStatus('Proforma');
    // Teklifi kaydet
    setTimeout(() => saveOfferWithItems(newItems, 'Proforma'), 100);
  };

  // Teklifi sil
  const deleteOffer = (id) => {
    const offers = savedOffers.filter(o => o.id !== id);
    localStorage.setItem('offers', JSON.stringify(offers));
    setSavedOffers(offers);
    if (selectedOfferId === id) setSelectedOfferId(null);
  };

  // Teklifi yükle
  const loadOffer = (offer) => {
    setRecipient(offer.recipient);
    setItems(offer.items);
    setNote(offer.note);
    setSelectedOfferId(offer.id);
    setStatus(offer.status || 'Taslak');
  };

  // Satır seçimini değiştir
  const toggleRowSelection = (idx) => {
    setSelectedRows((prev) =>
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };

  // Seçili satırların altına yeni satır ekle
  const addRowsAtSelected = () => {
    if (selectedRows.length === 0) return;
    let updated = [...items];
    // Her seçili satırın altına bir boş satır ekle
    selectedRows.sort((a, b) => b - a).forEach(idx => {
      updated.splice(idx + 1, 0, { ...emptyRow, vat: offerType === 'yurtdışı' ? 0 : defaultVat });
    });
    setItems(updated);
    setSelectedRows([]);
  };

  // Seçili satırları sil
  const removeSelectedRows = () => {
    if (selectedRows.length === 0) return;
    if (items.length - selectedRows.length < MIN_ROWS) return;
    let updated = items.filter((_, idx) => !selectedRows.includes(idx));
    setItems(updated);
    setSelectedRows([]);
  };

  // Tüm satırları temizle
  const clearAll = () => {
    setItems(Array.from({ length: MIN_ROWS }, () => ({ ...emptyRow, vat: offerType === 'yurtdışı' ? 0 : defaultVat })));
  };

  // UDI kodu oluşturucu
  function generateUDI(product, lot) {
    if (!product) return "";
    return [
      product.barcode,
      lot || product.lot,
      product.uretimTarihi,
      product.skt,
      product.dmoKodu,
      product.code
    ].filter(Boolean).join("-");
  }

  // Satırdaki alanı güncelle
  const updateItem = (index, field, value) => {
    const updated = [...items];
    if (field === 'code') {
      // SUT kodu çoklu seçim ise dizi olarak sakla
      updated[index][field] = Array.isArray(value) ? value : [value].filter(Boolean);
      // Ürün tanımını bul
      const product = allProducts.find(p => p.code === (Array.isArray(value) ? value[0] : value));
      if (product) {
        // Promosyon ise lot otomatik PROM ile başlasın
        let lot = product.lot;
        if (product.promo) {
          lot = `PROM${new Date().toISOString().slice(0,10).replace(/-/g,"")}-${index+1}`;
        }
        updated[index].lot = lot;
        // UDI kodunu oluştur
        updated[index].udi = generateUDI(product, lot);
        // Diğer alanları da otomatik doldur (fiyat, açıklama, vs.)
        updated[index].price = product.price;
        updated[index].description = product.description;
      }
    } else if (field === 'promo') {
      updated[index][field] = value && value.qty ? { qty: value.qty } : null;
    } else {
      updated[index][field] = value;
    }
    // Toplam adet = sipariş adedi + promosyon ürün adedi (varsa)
    const qty = Number(updated[index].quantity) || 0;
    const promoQty = updated[index].promo && updated[index].promo.qty ? Number(updated[index].promo.qty) : 0;
    updated[index].totalQty = qty + promoQty;
    // Sipariş tutarı = sadece gerçek adet * birim fiyat
    updated[index].total = qty * (Number(updated[index].price) || 0);
    setItems(updated);
  };

  // Popup aç
  const openPopup = (index, field) => {
    setEditingCell({ row: index, field });
    if (field === 'code') {
      // SUT kodu dizi ise dizi olarak popup'a aktar
      const val = items[index][field];
      setEditingValue(Array.isArray(val) ? val : (val ? [val] : []));
    } else if (field === 'promo') {
      setEditingValue(items[index][field] || { qty: '' });
    } else {
      setEditingValue(items[index][field] ?? "");
    }
  };

  // startEditCell fonksiyonu ekleniyor
  const startEditCell = (rowIdx, field) => {
    setEditingCell({ row: rowIdx, field });
    if (field === 'code') {
      const val = items[rowIdx][field];
      setEditingValue(Array.isArray(val) ? val : (val ? [val] : []));
    } else if (field === 'promo') {
      setEditingValue(items[rowIdx][field] || { qty: '' });
    } else {
      setEditingValue(items[rowIdx][field] ?? "");
    }
  };

  // Kaydetme fonksiyonu (blur veya Enter ile)
  const saveEditCell = () => {
    if (!editingCell) return;
    updateItem(editingCell.row, editingCell.field, editingValue);
    setEditingCell(null);
    setEditingValue("");
  };
  // Escape ile iptal
  const cancelEditCell = () => {
    setEditingCell(null);
    setEditingValue("");
  };

  // Hesaplamalar
  // const subtotal = items.reduce((sum, i) => sum + i.total, 0);
  // const totalVAT = items.reduce((sum, i) => sum + i.total * (i.vat || 0.2), 0);
  // const grandTotal = subtotal + totalVAT;

  // Autocomplete öneri güncelleme fonksiyonu
  const updateAutocompleteOptions = (field, value) => {
    let options = [];
    if (field === 'code') {
      const val = value?.toUpperCase?.() || '';
      options = allProducts.filter(p => p.code.toUpperCase().includes(val)).map(p => p.code);
    } else if (field === 'productName') {
      const val = value?.toLowerCase?.() || '';
      options = allProducts.filter(p => p.name.toLowerCase().includes(val)).map(p => p.name);
    }
    setAutocompleteOptions(options.slice(0, 8));
    setShowAutocomplete(options.length > 0);
  }

  const handleSendEmail = () => {
    const subject = encodeURIComponent(`Teklif: ${offerNo}`);
    const body = encodeURIComponent(`Merhaba,\n\nAşağıda detayları bulunan teklifimizi ekteki PDF dosyası ile iletiyoruz.\n\nAlıcı: ${recipient}\nTarih: ${getToday()}\nGenel Toplam: ${grandTotal}\n\nLütfen PDF dosyasını bu e-postaya ekleyiniz.\n\nİyi çalışmalar.`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const getPriceHistory = (field, value) => {
    if (!value) return [];
    let prices = [];
    savedOffers.forEach(offer => {
      (offer.items || []).forEach(item => {
        if (field === 'code' && (Array.isArray(item.code) ? item.code.includes(value) : item.code === value)) {
          prices.push(Number(item.price) || 0);
        }
        if (field === 'productName' && item.productName === value) {
          prices.push(Number(item.price) || 0);
        }
      });
    });
    return prices;
  };

  // Heroicons SVG ikonları
  const TrashIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-red-500 hover:text-red-700 transition">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );

  return (
    <>
      {/* HEADER: Flat kutu, logo ve hitap */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6 sm:mb-8 px-3 sm:px-8 py-4 sm:py-5" style={{ background: '#eaf7fe', borderRadius: '10px' }}>
        <img src="/invamedorder.svg" alt="INVAMED Order" className="h-10 mb-2 md:mb-0" />
        {readonly ? (
          recipient && (
          <div className="w-full md:w-auto text-right text-base font-medium bg-transparent px-3 py-2 rounded-none transition text-gray-700 border-0">
            {recipient}
          </div>
          )
        ) : (
          <input
            type="text"
            className="w-full md:w-auto text-right text-base font-medium bg-transparent px-3 py-2 rounded-none transition text-gray-700 border-0 focus:ring-0"
            value={recipient}
            onChange={e => setRecipient(e.target.value)}
            placeholder="Alıcı / Hitap"
            maxLength={60}
          />
        )}
      </div>
      {/* Tablo veya boş alan */}
      {items.length === 0 ? (
        readonly ? (
          <div className="w-full min-h-[50px] my-4"></div>
        ) : (
        <div
          className="w-full flex items-center justify-center border-2 border-dashed border-blue-300 rounded-xl h-[50px] my-4 text-blue-700 text-base font-semibold select-none cursor-pointer"
          style={{ minHeight: 50 }}
          onClick={() => setItems([{ ...emptyRow }])}
        >
          Satır eklemek için tıklayın
        </div>
        )
      ) : (
        <>
          {!readonly && (
            <div className="flex justify-end mb-2">
              <button
                type="button"
                className="px-4 py-2 rounded bg-blue-100 text-blue-700 font-semibold hover:bg-blue-200 transition text-sm"
                onClick={() => setItems(items => [...items, { ...emptyRow }])}
              >
                + Satır Ekle
              </button>
            </div>
          )}
        <div className="w-full overflow-x-auto">
          <table className="min-w-[600px] sm:min-w-full border-separate border-spacing-y-0 text-xs sm:text-sm">
            <thead>
              <tr className="bg-blue-100">
                {columns.map(col => (
                  <th key={col.key} className="px-3 py-2 text-left text-xs font-bold text-blue-950 bg-blue-100 tracking-wide">
                    {col.label}
                  </th>
                ))}
                  {!readonly && <th className="w-8 text-blue-950 font-bold bg-blue-100"></th>}
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? 'bg-[#eaf7fe]' : ''}>
                    {columns.map(col => (
                      <td
                        key={col.key}
                        className={
                          `px-3 py-2 align-middle whitespace-nowrap text-gray-700 text-sm border-0 ` +
                          (!readonly && col.key !== 'total' ? 'cursor-pointer transition group' : '')
                        }
                        onClick={() => !readonly && col.key !== 'total' && startEditCell(idx, col.key)}
                      >
                        {col.key === "total"
                          ? <span className="font-semibold text-gray-700">{formatCurrency(item.total, currency, customRates)}</span>
                          : col.key === "price"
                            ? formatCurrency(item.price, currency, customRates)
                            : col.key === "code"
                              ? renderSutCodes(item.code)
                              : col.key === "promo"
                                ? (item.promo && item.promo.qty
                                    ? <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded">{item.promo.qty} adet</span>
                                    : <span className="text-gray-300">-</span>)
                                : col.key === "vat"
                                  ? <span className="inline-block bg-blue-50 text-blue-800 px-2 py-0.5 rounded text-xs font-semibold">%{Math.round((item.vat || 0) * 100)}</span>
                                  : col.key === "description"
                                    ? (item[col.key] ? item[col.key] : <span className="text-gray-300">-</span>)
                                    : col.key === "udi"
                                      ? (item.udi || <span className="text-gray-300">-</span>)
                                      : (item[col.key] || "")
                        }
                      </td>
                    ))}
                    {!readonly && (
                  <td className="px-2 py-1 align-middle text-center">
                    <button
                      type="button"
                          className="p-1 rounded hover:bg-red-50 transition"
                      title="Satırı Sil"
                      onClick={() => setItems(items => items.filter((_, i) => i !== idx))}
                    >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-red-400 hover:text-red-600 transition">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                    </button>
                  </td>
                    )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
      {/* Not ve toplamlar fullwidth flat hizalı, mobilde alt alta */}
      <div className="flex flex-col md:flex-row gap-4 mt-4 w-full">
        <div className="flex-1 min-w-0">
          <div className="w-full bg-transparent px-3 py-2 text-gray-700 text-sm min-h-[60px] border-0 rounded-none shadow-none whitespace-pre-line">
            {note || <span className="text-gray-300">-</span>}
          </div>
        </div>
        <div className="flex-shrink-0 w-full md:w-[260px] flex justify-end md:justify-start items-end">
          <div className="px-3 sm:px-5 py-3 flex flex-col items-end gap-1 w-full md:w-[240px] mt-4 md:mt-0 rounded-none border-0 shadow-none bg-white/70">
            <div className="text-gray-500 text-xs">{t('Alt Toplam')}</div>
            <div className="font-semibold text-gray-700 text-sm mb-2">{formatCurrency(subtotal, currency, customRates)}</div>
            <div className="text-gray-500 text-xs">{t('KDV')}</div>
            <div className="font-semibold text-gray-700 text-sm mb-2">{formatCurrency(totalVAT, currency, customRates)}</div>
            <div className="text-gray-700 text-xs font-bold">{t('Genel Toplam')}</div>
            <div className="font-bold text-base text-gray-900">{formatCurrency(grandTotal, currency, customRates)}</div>
          </div>
        </div>
      </div>
      {!readonly && (
        <div className="flex justify-end mt-4">
          <button
            className="px-4 py-2 rounded bg-green-600 text-white font-semibold hover:bg-green-700 transition text-sm"
            onClick={handleProformaApprove}
            disabled={status === 'Proforma' || status === 'Kabul Edildi'}
          >
            Proforma Onayla
          </button>
        </div>
      )}
    </>
  );
}

export default function OfferForm({ currency: propCurrency = 'TRY', lang: propLang = 'TR', offerType: propOfferType = 'yurtiçi', recipient: propRecipient = '', onClose, onDelete, readonly = false, editMode = false, selectedOfferId = null, onSave, onEditWizard }) {
  // EN BAŞTA draft tanımı
  const draft = selectedOfferId ? JSON.parse(localStorage.getItem('offerDraft') || '{}') : {};
  const { user } = useUser();
  const { t, i18n } = useTranslation();
  React.useEffect(() => {
    if (propLang) i18n.changeLanguage(propLang);
  }, [propLang, i18n]);

  // 1. Tüm useState tanımlarını en başa al
  const [items, setItems] = useState(() => draft.items || []);
  const [selectedRows, setSelectedRows] = useState([]); // Seçili satır indexleri
  const [editingCell, setEditingCell] = useState(null); // {row: idx, field: key}
  const [editingValue, setEditingValue] = useState("");
  const [offerDate, setOfferDate] = useState(new Date().toLocaleDateString());
  const [offerNo, setOfferNo] = useState("2024-001");
  const [note, setNote] = useState(draft.note || "");
  const [savedOffers, setSavedOffers] = useState([]);
  const [status, setStatus] = useState('Taslak');
  const [history, setHistory] = useState([]); // İşlem geçmişi
  const [autocompleteOptions, setAutocompleteOptions] = useState([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const autocompleteRef = useRef();
  const VAT_OPTIONS = [
    { value: 0.01, label: '%1' },
    { value: 0.08, label: '%8' },
    { value: 0.10, label: '%10' },
    { value: 0.20, label: '%20' },
  ];
  const [defaultVat, setDefaultVat] = useState(0.20);
// Modal ve detay state'leri
const [showDetailModal, setShowDetailModal] = useState(false);
const [detailData, setDetailData] = useState(null);

  // 2. subtotal, totalVAT, grandTotal hesaplamalarını yap
  const subtotal = items.reduce((sum, i) => sum + i.total, 0);
  const totalVAT = items.reduce((sum, i) => sum + i.total * (i.vat || 0.2), 0);
  const grandTotal = subtotal + totalVAT;

  // 3. generateOfferNo fonksiyonunu tanımla
  function generateOfferNo() {
    const offers = JSON.parse(localStorage.getItem('offers') || '[]');
    const year = new Date().getFullYear();
    const last = offers.find(o => o.offerNo && o.offerNo.startsWith(year));
    let nextNo = 1;
    if (last) {
      const nums = offers
        .filter(o => o.offerNo && o.offerNo.startsWith(year))
        .map(o => parseInt(o.offerNo.split('-')[1], 10))
        .filter(Boolean);
      if (nums.length > 0) nextNo = Math.max(...nums) + 1;
    }
    return `${year}-${String(nextNo).padStart(3, '0')}`;
  }

  // 4. saveOfferWithItems fonksiyonunu tanımla
  const saveOfferWithItems = async (saveItems, saveStatus) => {
    if (saveItems.length === 0) return;
    const offer = {
      offerNo: generateOfferNo(),
      user_id: user?.id,
      date: getToday(),
      recipient,
      note,
      subtotal,
      totalVAT,
      grandTotal,
      status: saveStatus || status,
      emailSent: false,
      items: saveItems.map(item => ({
        product_id: item.product_id || null,
        measure: item.measure || '',
        qty: item.quantity || item.qty || 1,
        desc: item.description || item.desc || ''
      }))
    };
    try {
      const res = await fetch('/api/offers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(offer)
      });
      if (res.ok) {
        toast.showToast(t('Teklif başarıyla kaydedildi!'), 'success');
        localStorage.removeItem('offerDraft');
      } else {
        toast.showToast('Teklif kaydedilemedi!', 'error');
      }
    } catch (err) {
      toast.showToast('Teklif kaydedilemedi!', 'error');
    }
  };

  // Taslak sadece kayıtlı teklif açılırken kullanılsın
  // const draft = selectedOfferId ? JSON.parse(localStorage.getItem('offerDraft') || '{}') : {};

  // Yeni teklif başlatılırken taslak temizlensin
  useEffect(() => {
    if (!selectedOfferId) {
      localStorage.removeItem('offerDraft');
    }
  }, [selectedOfferId]);

  // Recipient state'i: kayıtlı teklif varsa taslaktan, yoksa sadece propRecipient
  const [recipient, setRecipient] = useState(() => {
    if (selectedOfferId) {
      return draft.recipient || propRecipient || '';
    }
    return propRecipient || '';
  });
  useEffect(() => {
    if (!selectedOfferId) setRecipient(propRecipient);
  }, [propRecipient, selectedOfferId]);
  // const [items, setItems] = useState(() => draft.items || []); // Moved up
  // const [selectedRows, setSelectedRows] = useState([]); // Moved up
  // const [editingCell, setEditingCell] = useState(null); // Moved up
  // const [editingValue, setEditingValue] = useState(""); // Moved up
  // const [offerDate, setOfferDate] = useState(new Date().toLocaleDateString()); // Moved up
  // const [offerNo, setOfferNo] = useState("2024-001"); // Moved up
  // const [note, setNote] = useState(draft.note || ""); // Moved up
  // const [savedOffers, setSavedOffers] = useState([]); // Moved up
  // const [status, setStatus] = useState('Taslak'); // Moved up
  // const [history, setHistory] = useState([]); // Moved up
  // const [autocompleteOptions, setAutocompleteOptions] = useState([]); // Moved up
  // const [showAutocomplete, setShowAutocomplete] = useState(false); // Moved up
  // const autocompleteRef = useRef(); // Moved up
  // const VAT_OPTIONS = [ // Moved up
  //   { value: 0.01, label: '%1' }, // Moved up
  //   { value: 0.08, label: '%8' }, // Moved up
  //   { value: 0.10, label: '%10' }, // Moved up
  //   { value: 0.20, label: '%20' }, // Moved up
  // ]; // Moved up
  // const [defaultVat, setDefaultVat] = useState(0.20); // Moved up

  // Eksik state ve ref/fonksiyonlar:
  const [offerType, setOfferType] = useState(propOfferType);
  const printRef = useRef();
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Teklif_${offerNo}`,
  });
  const toast = useToast();
  function getPdfBlob() {
    return new Promise((resolve) => {
      const printArea = printRef.current;
      const html = printArea.outerHTML;
      const blob = new Blob([html], { type: 'text/html' });
      resolve(blob);
    });
  }
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('Teklif');
  const [emailMessage, setEmailMessage] = useState('Merhaba, ekte teklif dosyanız bulunmaktadır.');
  const [emailSending, setEmailSending] = useState(false);
  async function handleSendEmailJS(e) {
    e.preventDefault();
    setEmailSending(true);
    const pdfBlob = await getPdfBlob();
    const reader = new FileReader();
    reader.onloadend = async function() {
      const base64data = reader.result.split(',')[1];
      const templateParams = {
        to_email: emailTo,
        subject: emailSubject,
        message: emailMessage,
        attachment: base64data,
        filename: 'teklif.html',
      };
      emailjs.send('service_demo123', 'template_demo123', templateParams, 'user_demo123')
        .then(() => {
          toast.showToast('E-posta başarıyla gönderildi!', 'success');
          setShowEmailModal(false);
          setEmailSending(false);
          // Son kaydedilen teklifi emailSent:true olarak işaretle
          const offers = JSON.parse(localStorage.getItem('offers') || '[]');
          if (offers.length > 0) {
            offers[0].emailSent = true;
            localStorage.setItem('offers', JSON.stringify(offers));
            setSavedOffers(offers);
          }
        }, (error) => {
          toast.showToast('E-posta gönderilemedi: ' + error.text, 'error');
          setEmailSending(false);
        });
    };
    reader.readAsDataURL(pdfBlob);
  }

  // UDI kodu oluşturucu
  function generateUDI(product, lot) {
    if (!product) return "";
    return [
      product.barcode,
      lot || product.lot,
      product.uretimTarihi,
      product.skt,
      product.dmoKodu,
      product.code
    ].filter(Boolean).join("-");
  }

  // Tablo columns dizisine UDI ekle
  const columns = offerType === 'yurtdışı'
    ? [
        { key: "productName", label: t('Ürün Adı') },
        { key: "quantity", label: t('Sipariş Adedi') },
        { key: "totalQty", label: t('Toplam Adet') },
        { key: "price", label: t('Birim Alış Fiyatı') },
        { key: "vat", label: t('KDV') },
        { key: "description", label: t('Açıklama') },
        { key: "total", label: t('Sipariş Tutarı') },
        { key: "udi", label: t('UDI Kodu') },
      ]
    : [
        { key: "code", label: t('SUT Kodu') },
        { key: "quantity", label: t('Sipariş Adedi') },
        { key: "promo", label: t('Promosyon Ürün') },
        { key: "totalQty", label: t('Toplam Adet') },
        { key: "price", label: t('Birim Alış Fiyatı') },
        { key: "vat", label: t('KDV') },
        { key: "description", label: t('Açıklama') },
        { key: "total", label: t('Sipariş Tutarı') },
        { key: "udi", label: t('UDI Kodu') },
      ];
  // useState başlatırken localStorage'dan taslak yükle
  // const draft = JSON.parse(localStorage.getItem('offerDraft') || '{}');
  // const [items, setItems] = useState(() => draft.items || []);
  // const [selectedRows, setSelectedRows] = useState([]); // Seçili satır indexleri
  // Popup state'leri kaldırılıyor, yerine inline edit için state ekleniyor
  // const [editingCell, setEditingCell] = useState(null); // {row: idx, field: key}
  // const [editingValue, setEditingValue] = useState("");
  // const [recipient, setRecipient] = useState(draft.recipient || "Sayın Müşterimiz");
  // const [offerDate] = useState(new Date().toLocaleDateString());
  // const [offerNo] = useState("2024-001");
  // const [note, setNote] = useState(draft.note || "");
  // const [savedOffers, setSavedOffers] = useState([]);
  // const [selectedOfferId, setSelectedOfferId] = useState(null);
  // const [offerType, setOfferType] = useState(draft.offerType || 'yurtiçi');
  // const [currency, setCurrency] = useState(draft.currency || 'TRY');
  // const [customRates, setCustomRates] = useState(draft.customRates || { USD: 32, EUR: 35 });
  // const printRef = useRef();
  // const handlePrint = useReactToPrint({
  //   content: () => printRef.current,
  //   documentTitle: `Teklif_${offerNo}`,
  // });
  // const toast = useToast();
  // const [showPdfPreview, setShowPdfPreview] = useState(false);
  // const OFFER_STATUSES = [
  //   { value: 'Taslak', color: 'bg-gray-200 text-gray-700' },
  //   { value: 'Gönderildi', color: 'bg-blue-100 text-blue-800' },
  //   { value: 'Kabul Edildi', color: 'bg-green-100 text-green-800' },
  //   { value: 'Reddedildi', color: 'bg-red-100 text-red-700' },
  // ];
  // const [status, setStatus] = useState('Taslak');
  // const [autocompleteOptions, setAutocompleteOptions] = useState([]);
  // const [showAutocomplete, setShowAutocomplete] = useState(false);
  // const autocompleteRef = useRef();
  // const VAT_OPTIONS = [
  //   { value: 0.01, label: '%1' },
  //   { value: 0.08, label: '%8' },
  //   { value: 0.10, label: '%10' },
  //   { value: 0.20, label: '%20' },
  // ];
  // const [defaultVat, setDefaultVat] = useState(0.20);

  // selectedOfferId varsa ilgili teklifi yükle
  useEffect(() => {
    if (selectedOfferId) {
      const offers = JSON.parse(localStorage.getItem('offers') || '[]');
      const offer = offers.find(o => String(o.id) === String(selectedOfferId));
      if (offer) {
        setRecipient(offer.recipient);
        setItems(offer.items);
        setNote(offer.note);
        setOfferDate(offer.date);
        setOfferNo(offer.offerNo);
        setStatus(offer.status || 'Taslak');
        setHistory(offer.history || []);
      }
    }
  }, [selectedOfferId]);

  // Düzenleme modunda teklif detayını API'den çek
  useEffect(() => {
    if (editMode && selectedOfferId) {
      fetch(`/api/offers/${selectedOfferId}`)
        .then(res => res.json())
        .then(data => {
          setRecipient(data.recipient || '');
          setItems(data.items || []);
          setNote(data.note || '');
          setOfferDate(data.date || '');
          setOfferNo(data.offerNo || '');
          setStatus(data.status || 'Taslak');
        });
    }
  }, [editMode, selectedOfferId]);

  // Kaydet işlemi: mevcut teklifin üzerine yaz ve işlem geçmişine ekle
  const handleSave = async () => {
    if (items.length === 0) return;
    const offer = {
      offerNo,
      user_id: user?.id,
      date: offerDate,
      recipient,
      note,
      subtotal,
      totalVAT,
      grandTotal,
      status,
      emailSent: false,
      items: items.map(item => ({
        product_id: item.product_id || null,
        measure: item.measure || '',
        qty: item.quantity || item.qty || 1,
        desc: item.description || item.desc || ''
      }))
    };
    try {
      const res = await fetch(`/api/offers/${selectedOfferId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(offer)
      });
      if (res.ok) {
      if (onSave) onSave();
        toast.showToast('Teklif başarıyla güncellendi!', 'success');
      } else {
        toast.showToast('Teklif güncellenemedi!', 'error');
      }
    } catch (err) {
      toast.showToast('Teklif güncellenemedi!', 'error');
    }
  };

  // Proformayı kaydet fonksiyonu
  const handleProformaSave = () => {
    if (items.length === 0 || !items.some(row => row.code && row.code.length > 0)) {
      toast.showToast('En az bir ürün seçmelisiniz.', 'warning');
      return;
    }
    setStatus('Proforma');
    saveOfferWithItems(items, 'Proforma');
  };
  // Proformayı onayla fonksiyonu
  const handleProformaApprove = () => {
    if (status !== 'Proforma') return;
    // UDI kodlarını oluştur
    const newItems = items.map((item, idx) => {
      if (!item.udi) {
        // Ürün tanımını bul
        const code = Array.isArray(item.code) ? item.code[0] : item.code;
        const product = allProducts.find(p => p.code === code);
        let lot = item.lot || (product ? product.lot : '');
        if (product && product.promo) {
          lot = `PROM${new Date().toISOString().slice(0,10).replace(/-/g,"")}-${idx+1}`;
        }
        return {
          ...item,
          lot,
          udi: generateUDI(product, lot)
        };
      }
      return item;
    });
    setItems(newItems);
    setStatus('Onaylandı');
    setTimeout(() => saveOfferWithItems(newItems, 'Onaylandı'), 100);
  };

  // UDI’leri oluştur fonksiyonu
  const handleUDIApprove = () => {
    if (status !== 'Onaylandı') return;
    // UDI kodlarını oluştur
    const newItems = items.map((item, idx) => {
      if (!item.udi) {
        // Ürün tanımını bul
        const code = Array.isArray(item.code) ? item.code[0] : item.code;
        const product = allProducts.find(p => p.code === code);
        let lot = item.lot || (product ? product.lot : '');
        if (product && product.promo) {
          lot = `PROM${new Date().toISOString().slice(0,10).replace(/-/g,"")}-${idx+1}`;
        }
        return {
          ...item,
          lot,
          udi: generateUDI(product, lot)
        };
      }
      return item;
    });
    setItems(newItems);
    toast.showToast('UDI kodları oluşturuldu!', 'success');
    // İsterseniz burada tekrar kaydedebilirsiniz
  };

  // OfferForm fonksiyonu içinde handleDeleteDraft fonksiyonunu tanımla
  const handleDeleteDraft = () => {
    if (window.confirm('Taslağı silmek istediğinize emin misiniz?')) {
      // Silinen taslağı kaydet
      const deletedDrafts = JSON.parse(localStorage.getItem('deletedDrafts') || '[]');
      deletedDrafts.unshift({
        date: new Date().toLocaleString(),
        recipient,
        items,
        note,
        status,
        offerType,
        offerNo,
      });
      localStorage.setItem('deletedDrafts', JSON.stringify(deletedDrafts));
      localStorage.removeItem('offerDraft');
      if (typeof onDelete === 'function') onDelete();
    }
  };

  // Formun başında mod göstergesi ve düzenle butonu
  return (
    <div className="relative pt-20 sm:pt-24 w-full max-w-7xl mx-auto p-1 sm:p-0 min-h-[80vh]">
      <div className="flex flex-wrap gap-2 items-center mb-4 p-3 bg-blue-50 rounded-xl shadow text-blue-900 text-sm font-semibold">
        <span>Para Birimi: <span className="font-bold">{propCurrency}</span></span>
        <span>|</span>
        <span>Dil: <span className="font-bold">{propLang === 'EN' ? 'English' : 'Türkçe'}</span></span>
        <span>|</span>
        <span>Teklif Tipi: <span className="font-bold">{propOfferType === 'yurtdışı' ? 'Yurtdışı' : 'Yurtiçi'}</span></span>
        {readonly && <span className="ml-4 px-2 py-1 bg-gray-200 text-gray-600 rounded">Salt Okunur</span>}
        {editMode && <span className="ml-4 px-2 py-1 bg-yellow-200 text-yellow-700 rounded">Düzenleme Modu</span>}
        <span className="flex-1"></span>
        {!readonly && (
          <>
            <button
              type="button"
              className="mr-2 px-4 py-2 rounded bg-red-100 text-red-700 font-semibold hover:bg-red-200 transition text-sm"
              onClick={handleDeleteDraft}
            >
              Taslağı Sil
            </button>
            <button
              type="button"
              className="ml-auto px-4 py-2 rounded bg-blue-600 text-white font-semibold hover:bg-blue-700 transition text-sm"
              onClick={() => typeof onEditWizard === 'function' ? onEditWizard() : alert('Sihirbaz düzenleme fonksiyonu tanımlı değil!')}
            >
              Sihirbazı Düzenle
            </button>
          </>
        )}
      </div>
      {/* PDF'ye Aktar ve Teklifi Kaydet butonları */}
      <div className="flex flex-col sm:flex-row justify-end mb-2 gap-2">
        <button
          onClick={() => setShowPdfPreview(true)}
          className="w-full sm:w-auto px-4 py-3 sm:py-2 rounded-xl bg-gray-200 text-blue-950 font-semibold shadow hover:bg-gray-300 transition text-base sm:text-sm"
        >
          {t('PDF Önizleme')}
        </button>
        <button
          onClick={handlePrint}
          className="w-full sm:w-auto px-4 py-3 sm:py-2 rounded-xl bg-blue-600 text-white font-semibold shadow hover:bg-blue-700 transition text-base sm:text-sm"
        >
          {t('PDF\'ye Aktar')}
        </button>
        <button
          onClick={() => setShowEmailModal(true)}
          className="w-full sm:w-auto px-4 py-3 sm:py-2 rounded-xl bg-yellow-400 text-blue-950 font-semibold shadow hover:bg-yellow-500 transition text-base sm:text-sm"
        >
          {t('E-posta ile Gönder')}
        </button>
        {editMode && (
          <button
            className="w-full sm:w-auto px-4 py-3 sm:py-2 rounded-xl bg-green-600 text-white font-semibold shadow hover:bg-green-700 transition text-base sm:text-sm"
            onClick={handleSave}
            disabled={items.length === 0}
          >
            {t('Kaydet')}
          </button>
        )}
      </div>
      {/* PDF Önizleme Modalı */}
      {showPdfPreview && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          {/* Çarpı işareti modal kutusunun dışında */}
            <button
            className="absolute top-8 right-[calc(50%-2.5rem)] md:right-[calc(50%-32rem)] px-4 py-2 rounded-full bg-gray-200 text-gray-700 hover:bg-gray-300 font-bold text-2xl z-50 shadow-lg"
              onClick={() => setShowPdfPreview(false)}
              aria-label="Kapat"
            style={{ lineHeight: 1, transform: 'translateX(50%)' }}
            >×</button>
          <div className="bg-white dark:bg-gray-900 w-full max-w-5xl max-h-[90vh] flex flex-col border border-blue-100 overflow-hidden relative shadow-2xl rounded-2xl items-center justify-center" style={{ minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="flex-1 w-full h-full flex items-center justify-center overflow-auto p-0">
              {/* PrintContent sadece readonly=true ile gösterilecek, başka hiçbir buton veya input olmayacak */}
              <div className="w-full max-w-[1120px] max-h-[793px] bg-white shadow-xl border border-gray-200 overflow-auto p-0 flex flex-col justify-start mx-auto my-auto" style={{ boxSizing: 'border-box', minHeight: 400 }}>
                <PrintContent offerType={offerType} readonly={true} />
                </div>
            </div>
          </div>
        </div>
      )}
      {/* Yazdırılacak alan */}
      <div ref={printRef}>
        <PrintContent offerType={offerType} readonly={readonly} />
      </div>
      {/* Popup editör ve ilgili state'ler kaldırılacak */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <form onSubmit={handleSendEmailJS} className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md flex flex-col gap-4 border border-blue-100">
            <div className="text-xl font-bold text-blue-900 text-center mb-2">E-posta ile Gönder</div>
            <input
              type="email"
              placeholder="Alıcı E-posta"
              className="px-4 py-3 rounded-lg border border-blue-100 focus:ring-2 focus:ring-blue-200 text-blue-900 bg-blue-50"
              value={emailTo}
              onChange={e => setEmailTo(e.target.value)}
              required
            />
            <input
              type="text"
              placeholder="Konu"
              className="px-4 py-3 rounded-lg border border-blue-100 focus:ring-2 focus:ring-blue-200 text-blue-900 bg-blue-50"
              value={emailSubject}
              onChange={e => setEmailSubject(e.target.value)}
              required
            />
            <textarea
              placeholder="Mesaj"
              className="px-4 py-3 rounded-lg border border-blue-100 focus:ring-2 focus:ring-blue-200 text-blue-900 bg-blue-50 min-h-[80px]"
              value={emailMessage}
              onChange={e => setEmailMessage(e.target.value)}
              required
            />
            <div className="text-xs text-gray-500">PDF dosyası otomatik olarak eklenecek.</div>
            <div className="flex gap-2 mt-2">
              <button type="button" className="flex-1 px-4 py-2 rounded bg-gray-200 text-blue-900 font-semibold hover:bg-gray-300 transition" onClick={() => setShowEmailModal(false)} disabled={emailSending}>İptal</button>
              <button type="submit" className="flex-1 px-4 py-2 rounded bg-blue-600 text-white font-semibold hover:bg-blue-700 transition" disabled={emailSending}>{emailSending ? 'Gönderiliyor...' : 'Gönder'}</button>
            </div>
          </form>
        </div>
      )}
      {/* Tablo altına butonları statüye göre göster */}
      {!readonly && (
        <div className="flex flex-col sm:flex-row gap-2 justify-end mt-4">
          {(status === 'Taslak' || status === 'Gönderildi') && (
            <button
              className="px-4 py-2 rounded bg-blue-600 text-white font-semibold hover:bg-blue-700 transition text-sm"
              onClick={handleProformaSave}
              disabled={items.length === 0 || !items.some(row => row.code && row.code.length > 0)}
            >
              Proformayı Kaydet
            </button>
          )}
          {status === 'Proforma' && (
            <button
              className="px-4 py-2 rounded bg-green-600 text-white font-semibold hover:bg-green-700 transition text-sm"
              onClick={handleProformaApprove}
            >
              Proformayı Onayla
            </button>
          )}
          {status === 'Onaylandı' && (
            <button
              className="px-4 py-2 rounded bg-purple-600 text-white font-semibold hover:bg-purple-700 transition text-sm"
              onClick={handleUDIApprove}
            >
              UDI’leri Oluştur
            </button>
          )}
        </div>
      )}
      {/* Sayfanın altında Silinen Taslaklar listesini göster */}
      {!readonly && (
        <div className="mt-8">
          <h3 className="text-lg font-bold text-red-700 mb-2">Silinen Taslaklar</h3>
          <div className="bg-red-50 rounded-xl p-4 shadow-inner">
            {JSON.parse(localStorage.getItem('deletedDrafts') || '[]').length === 0 ? (
              <div className="text-gray-400">Hiç silinen taslak yok.</div>
            ) : (
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="bg-red-100 text-red-800">
                    <th className="px-2 py-1 text-left">Tarih</th>
                    <th className="px-2 py-1 text-left">Alıcı</th>
                    <th className="px-2 py-1 text-left">Not</th>
                    <th className="px-2 py-1 text-left">Statü</th>
                    <th className="px-2 py-1 text-left">Teklif No</th>
                    <th className="px-2 py-1 text-left">Ürün Sayısı</th>
                    <th className="px-2 py-1 text-left">Görüntüle</th>
                  </tr>
                </thead>
                <tbody>
                  {JSON.parse(localStorage.getItem('deletedDrafts') || '[]').map((d, i) => (
                    <tr key={i}>
                      <td className="px-2 py-1">{d.date}</td>
                      <td className="px-2 py-1">{d.recipient}</td>
                      <td className="px-2 py-1">{d.note}</td>
                      <td className="px-2 py-1">{d.status}</td>
                      <td className="px-2 py-1">{d.offerNo}</td>
                      <td className="px-2 py-1">{d.items?.length || 0}</td>
                      <td className="px-2 py-1">
                        <button className="px-2 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 text-xs font-semibold" onClick={() => { setDetailData(d); setShowDetailModal(true); }}>Görüntüle</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
      {/* Kayıtlı Teklifler tablosu */}
      {savedOffers.length > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-bold text-blue-700 mb-2">Kayıtlı Teklifler</h3>
          <div className="bg-blue-50 rounded-xl p-4 shadow-inner">
            {savedOffers.length === 0 ? (
              <div className="text-gray-400">Kayıtlı teklif bulunmamaktadır.</div>
            ) : (
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="bg-blue-100 text-blue-800">
                    <th className="px-2 py-1 text-left">Tarih</th>
                    <th className="px-2 py-1 text-left">Alıcı</th>
                    <th className="px-2 py-1 text-left">Not</th>
                    <th className="px-2 py-1 text-left">Statü</th>
                    <th className="px-2 py-1 text-left">Teklif No</th>
                    <th className="px-2 py-1 text-left">Ürün Sayısı</th>
                    <th className="px-2 py-1 text-left">Görüntüle</th>
                  </tr>
                </thead>
                <tbody>
                  {savedOffers.map((offer, i) => (
                    <tr key={offer.id} className={i % 2 === 0 ? 'bg-[#eaf7fe]' : ''}>
                      <td className="px-2 py-1">{offer.date}</td>
                      <td className="px-2 py-1">{offer.recipient}</td>
                      <td className="px-2 py-1">{offer.note}</td>
                      <td className="px-2 py-1">{offer.status}</td>
                      <td className="px-2 py-1">{offer.offerNo}</td>
                      <td className="px-2 py-1">{offer.items?.length || 0}</td>
                      <td className="px-2 py-1">
                        <button className="px-2 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 text-xs font-semibold" onClick={() => { setDetailData(offer); setShowDetailModal(true); }}>Görüntüle</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
      {/* Modal ile detay gösterimi */}
      {showDetailModal && detailData && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-2xl flex flex-col gap-4 border border-blue-100">
            <div className="text-xl font-bold text-blue-900 text-center mb-2">Teklif/Taslak Detayı</div>
            <div className="flex flex-col gap-2 text-sm">
              <div><b>Tarih:</b> {detailData.date}</div>
              <div><b>Alıcı:</b> {detailData.recipient}</div>
              <div><b>Not:</b> {detailData.note}</div>
              <div><b>Statü:</b> {detailData.status}</div>
              <div><b>Teklif No:</b> {detailData.offerNo}</div>
              <div><b>Ürünler:</b>
                <ul className="list-disc ml-6">
                  {(detailData.items || []).map((item, idx) => (
                    <li key={idx}>{item.code || item.productName} - {item.quantity} adet</li>
                  ))}
                </ul>
              </div>
            </div>
            <button className="mt-4 px-4 py-2 rounded bg-blue-600 text-white font-semibold hover:bg-blue-700 transition" onClick={() => setShowDetailModal(false)}>Kapat</button>
          </div>
        </div>
      )}
    </div>
  );
} 